import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ControlOrganizationLevelSeg } from "src/app/models/controlOrganizationLevelSeg.model";
import { ControlOrganizationLevelSegService } from "src/app/services/controlOrganizationLevelSeg.service";
import { FormComponent, IFormDialogData } from "src/app/shared/form/form.component";
import {
    DateFormField,
    HierarchicalSelectionFormField,
    SingleSelectionFormField,
    TextAreaFormField
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { SegService } from "src/app/services/seg.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { forkJoin } from "rxjs";
import { toOrganizationTree } from "src/app/_helpers/org-tree-helper";
import { OrganizationLevel } from "src/app/models/organizationLevel.model";
import { ControlStatusEnum } from "src/app/enums/control-status.enum";
import { getEnumLookup } from "src/app/_helpers/enum-helper";

@Component({
  standalone: false,
    selector: 'app-control-org-level-seg-dialog',
    templateUrl: './../../shared/form/form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./../../shared/form/form.component.scss'],
})
export class ControlOrgLevelSegDialogComponent extends FormComponent<ControlOrganizationLevelSeg> implements OnInit {
    segs: ILookup[] = [];
    organizationLevels: OrganizationLevel[] = [];
    statusOptions: ILookup[] = [];

    constructor(
        dialogRef: MatDialogRef<ControlOrgLevelSegDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        controlOrgLevelSegService: ControlOrganizationLevelSegService,
        protected injector: Injector,
        private segService: SegService,
        private organizationLevelService: OrganizationLevelService
    ) {
        super(dialogRef, controlOrgLevelSegService, data, 'Control SEG', injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        this.statusOptions = getEnumLookup(ControlStatusEnum);
        forkJoin({
            segs: this.segService.getAll(),
            organizationLevels: this.organizationLevelService.getAll(),
            entity: this.getEntity()
        }).subscribe({
            next: result => {
                this.segs = result.segs;
                this.organizationLevels = result.organizationLevels;
                this.entity = result.entity;
                this.initModel();
                this.initFields();
            },
            error: error => {
                this.toastService.showError(error);
                this.isFormLoading = false;
            },
            complete: () => {
                this.isFormLoading = false;
            }
        });
    }

    getNewObject(): ControlOrganizationLevelSeg {
        return new ControlOrganizationLevelSeg();
    }

    initModel(): void {
        if (this.isAdd) {
            const { controlId } = this.data as IFormDialogData & { controlId?: string };
            if (controlId) {
                this.entity.controlId = controlId;
            }
        }
    }

    validate(): boolean {
        return true;
    }

    onBeforeSave(model: ControlOrganizationLevelSeg): void {
        const formatDate = (value?: Date | string | null): string | null => {
            if (!value) {
                return null;
            }
            
            return this.dateFormatService.parseUserDateToYMD(value.toString());
        }

        const dateFields: (keyof ControlOrganizationLevelSeg)[] = [
            'dateImplementationStart',
            'dateImplementationComplete',
            'targetImplementationStart',
            'targetImplementationEnd'
        ];

        dateFields.forEach(field => {
            model[field as string] = formatDate(model[field] as Date | null);
        });
    }

    initFields(): void {
        this.fields = [
            new SingleSelectionFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.segId),
                options: this.segs,
                name: 'SEG',
                required: true
            }),
            new HierarchicalSelectionFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.organizationLevelId),
                options: toOrganizationTree(this.organizationLevels),
                name: 'Organization Level',
                required: true
            }),
            new SingleSelectionFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.status),
                options: this.statusOptions,
                name: 'Status',
                required: true
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.targetImplementationStart),
                name: 'Target Implementation Start Date',
                required: false
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.targetImplementationEnd),
                name: 'Target Implementation End Date',
                required: false
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.dateImplementationStart),
                name: 'Implementation Start Date',
                required: false
            }),
            new DateFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.dateImplementationComplete),
                name: 'Implementation Complete Date',
                required: false
            }),
            new TextAreaFormField({
                property: nameOf<ControlOrganizationLevelSeg>(x => x.notes),
                name: 'Notes',
                required: false
            })
        ];
    }
}
